import React from 'react';
import { createRoot } from 'react-dom/client';
import { createStore, applyMiddleware, compose } from 'redux';
import thunk from 'redux-thunk';
import { Provider } from 'react-redux';
import './index.css';
import App from './Components/App/App';
import WrappedMuxProvider from './Components/WrappedMuxProvider/WrappedMuxProvider';
import reportWebVitals from './reportWebVitals';
import { Logger } from '@manulife/alf-ui-js';
import registerFetchAdapter from './TestAdapters/FetchAdapter';
import RootReducer from './StateSetup/RootReducer';
import GetInitialState from './StateSetup/GetInitialState';
import '@manulife/mux/core/typography/assets/fonts/fonts.css';

/**
 * Create a logger instance. This has part of the context but more context is set after the user has authenticated.
 */
const logger = new Logger(
    {
        syslog: {
            hostName: window.location.hostname,
            appName: window.__ENV__.ULS_APP_NAME,
        },
        correlation: {
            channel: 'web',
        },
        ULS: {
            applicationCINumber: window.__ENV__.ULS_APP_CI,
            environmentCINumber: window.__ENV__.ULS_ENVIRONMENT_CI,
            environment: window.__ENV__.ULS_ENVIRONMENT,
        },
    },
    {
        remoteURL: `${window.__ENV__.BFF_URL}/log/v3/send`,
    }
);

// If we are running the app in a mock mode, register the Fetch Adapter.
// This overrides the response from our backend services with local data mocks.
// Note: This code will be considered dead and completely removed from environments where this flag is set to false.
window.__ENV__.TEST_MOCK_MODE === 'true' &&
    registerFetchAdapter({ testCasesFolderPath: './TestCases/', ignoreUrls: [process.env.PUBLIC_URL] });

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

// Create the Redux Store. The Initial State is a skeleton for what the state tree will look like.
const store = createStore(
    RootReducer,
    GetInitialState(),
    composeEnhancers(applyMiddleware(thunk.withExtraArgument({ logger })))
);

const container = document.getElementById('root');
const root = createRoot(container);
root.render(
    <Provider store={store}>
        <WrappedMuxProvider>
            <App logger={logger} />
        </WrappedMuxProvider>
    </Provider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
